import { Injectable, NgZone } from '@angular/core';

export interface Content {
    'id': string;
    'quantity': number;
}
export interface AddPaymentInfoData {
    contentCategory: string;
    contentIds: Array<number>;
    contentType: string;
    contents: Array<Content>;
    value: number;
}
export interface InitiateCheckoutData {
    contentIds: Array<number>;
    contentType: string;
    contentCategory: string;
    contents: Array<Content>;
    numItems: number;
    value: number;
}
export interface PurchaseData {
    value: string;
    contentIds: Array<number>;
    contentType: string;
    contentName: string;
    contents: Array<Content>;
    numItems: number;
}
export interface AddToCartData {
    value: string;
    contentIds: Array<number>;
    contentType: string;
    contentName: string;
    contents: Array<Content>;
}

@Injectable()
export class FacebookService {
    public CONTENT_TYPE_PRODUCT = 'product';
    public DEFAULT_CURRENCY = 'BRL';

    protected facebookConversionId: number = null;

    protected trackPageViewQueue = [];
    protected trackAddPaymentInfoQueue: Array<AddPaymentInfoData> = [];
    protected trackConversionQueue: Array<PurchaseData> = [];
    protected trackInitiateCheckoutQueue: Array<InitiateCheckoutData> = [];
    protected trackAddToCartQueue: Array<AddToCartData> = [];

    constructor(protected zone: NgZone) { }

    /** Capture facebook conversion id from store configurations data if has */
    public configure(data) {
        if (
            data['facebook-conversion-id'] !== undefined
            && data['facebook-conversion-id'] !== null
        ) {
            this.facebookConversionId = data['facebook-conversion-id'];

            /**
             * When page url track is faster then configuration request.
             * All tracks should be queued and process by commands below.
             **/
            this.processQueues();
        }
    }
    public trackPageView() {
        if (this.canTrack()) {
            this.track('PageView');
        } else {
            this.trackPageViewQueue.push({});
        }
    }
    public trackAddPaymentInfo(
        contentCategory: string,
        contentIds: Array<number>,
        contentType: string,
        contents: Array<Content>,
        value: number
    ) {
        if (this.canTrack()) {
            this.track('AddPaymentInfo', {
                content_category: contentCategory,
                content_ids: contentIds,
                content_type: contentType,
                contents: contents,
                currency: this.DEFAULT_CURRENCY,
                value: value
            });
        } else {
            this.trackAddPaymentInfoQueue.push({
                contentCategory: contentCategory,
                contentIds: contentIds,
                contentType: contentType,
                contents: contents,
                value: value
            });
        }
    }
    public trackInitiateCheckout(
        contentIds: Array<number>,
        contentType: string,
        contentCategory: string,
        contents: Array<Content>,
        numItems: number,
        value: number
    ) {
        if (this.canTrack()) {
            this.track('InitiateCheckout', {
                content_ids: contentIds,
                content_type: contentType,
                content_category: contentCategory,
                currency: this.DEFAULT_CURRENCY,
                contents: contents,
                num_items: numItems,
                value: value
            });
        } else {
            this.trackInitiateCheckoutQueue.push({
                contentIds: contentIds,
                contentType: contentType,
                contentCategory: contentCategory,
                contents: contents,
                numItems: numItems,
                value: value
            });
        }
    }
    public trackConversion(
        value: string,
        contentIds: Array<number>,
        contentType: string,
        contentName: string,
        contents: Array<Content>,
        numItems: number
    ) {
        if (this.canTrack()) {
            this.track('Purchase', {
                value: `${+value}`,
                currency: this.DEFAULT_CURRENCY,
                content_ids: contentIds,
                content_type: contentType,
                content_name: contentName,
                contents: contents,
                num_items: numItems
            });
        } else {
            this.trackConversionQueue.push({
                value: value,
                contentIds: contentIds,
                contentType: contentType,
                contentName: contentName,
                contents: contents,
                numItems: numItems
            });
        }
    }
    public trackAddToCart(
        value: string,
        contentIds: Array<number>,
        contentType: string,
        contentName: string,
        contents: Array<Content>
    ) {
        if (this.canTrack()) {
            this.track('AddToCart', {
                value: `${+value}`,
                currency: this.DEFAULT_CURRENCY,
                content_ids: contentIds,
                content_type: contentType,
                content_name: contentName,
                contents: contents
            });
        } else {
            this.trackAddToCartQueue.push({
                value: value,
                contentIds: contentIds,
                contentType: contentType,
                contentName: contentName,
                contents: contents
            });
        }
    }

    /**
     * Track queue items stored to be tracked async.
     **/
    private processQueues() {
        /*
         | Track Purchase events
         */
        if (this.trackConversionQueue.length > 0) {
            this.trackConversionQueue.forEach((data: PurchaseData) => {
                this.trackConversion(
                    `${data.value}`,
                    data.contentIds,
                    data.contentType,
                    data.contentName,
                    data.contents,
                    data.numItems
                );
            });
            this.trackConversionQueue = [];
        }

        /*
         | Track InitiateCheckout events
         */
        if (this.trackInitiateCheckoutQueue.length > 0) {
            this.trackInitiateCheckoutQueue.forEach((data: InitiateCheckoutData) => {
                this.trackInitiateCheckout(
                    data.contentIds,
                    data.contentType,
                    data.contentCategory,
                    data.contents,
                    data.numItems,
                    data.value
                );
            });
            this.trackInitiateCheckoutQueue = [];
        }

        /*
         | Track PageView events
         */
        if (this.trackPageViewQueue.length > 0) {
            this.trackPageViewQueue.forEach(() => this.trackPageView());
            this.trackPageViewQueue = [];
        }

        /*
         | Track AddPaymentInfo events
         */
        if (this.trackAddPaymentInfoQueue.length > 0) {
            this.trackAddPaymentInfoQueue.forEach((data: AddPaymentInfoData) => this.trackAddPaymentInfo(
                data.contentCategory,
                data.contentIds,
                data.contentType,
                data.contents,
                data.value
            ));
            this.trackAddPaymentInfoQueue = [];
        }
    }

    /**
     * Get ip v6
     */
    private getIPv6(): string | null {
        let ipv6: string | null = null;
    
        const RTCPeerConnection = window['RTCPeerConnection'] || window['webkitRTCPeerConnection'] || window['mozRTCPeerConnection'];
    
        if (RTCPeerConnection) {
            const peerConnection = new RTCPeerConnection({ iceServers: [] });
    
            peerConnection.createDataChannel('');
            peerConnection.createOffer()
                .then(offer => peerConnection.setLocalDescription(offer))
                .catch(error => console.error('Error setting local description:', error));
    
            peerConnection.onicecandidate = event => {
                if (event && event.candidate && event.candidate.candidate) {
                    const candidateParts = event.candidate.candidate.split(' ');
                    const address = candidateParts[4];
                    if (address && address.includes(':') && !address.includes('.')) {
                        ipv6 = address;
                        peerConnection.close();
                    }
                }
            };
        }
    
        return ipv6;
    }

    /**
     * Call fbq track method.
     **/
    private track(event: string, details: any = null) {
        this.zone.run(() => {
            const ipv6 = this.getIPv6();
            if (details !== null) {
                if (ipv6) {
                    details['client_ip_address'] = ipv6;
                }
                return window['fbq']('track', event, details);
            }

            return window['fbq']('track', event);
        });
    }
    /**
     * Check if fb script is imported and pixel is defined.
     **/
    private canTrack(): boolean {
        return window['fbq'] !== undefined &&
            window['fbq'] !== null &&
            this.facebookConversionId !== undefined &&
            this.facebookConversionId !== null;
    }
}



// WEBPACK FOOTER //
// /codebuild/output/src2032928750/src/src/app/shared/services/facebook.service.ts